(() => {
  'use strict';
  if (window.PinmeNavigationV4) { window.PinmeNavigationV4.init(document); return; }
  const instances = new Map();
  const visible = (node) => {
    if (!node?.isConnected || node.closest('[hidden],[inert]') || !node.getClientRects().length || getComputedStyle(node).visibility === 'hidden') return false;
    for (let details = node.parentElement?.closest('details:not([open])'); details; details = details.parentElement?.closest('details:not([open])')) {
      if (!details.querySelector(':scope > summary')?.contains(node)) return false;
    }
    return true;
  };
  const focusable = (root) => [...root.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),summary,[tabindex="0"]')].filter(visible);
  const initHeader = (root) => {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const listen = (node, event, callback, options = {}) => node?.addEventListener(event, callback, { ...options, signal: controller.signal });
    const mobileHead = root.querySelector('[data-pinme-mobile-head]');
    const desktopHead = root.querySelector('[data-pinme-desktop-head]');
    const drawer = root.querySelector('[data-pinme-drawer]');
    const panel = drawer?.querySelector('.pinme-drawer__panel');
    const flow = drawer?.querySelector('.pinme-drawer__flow');
    const openButton = root.querySelector('[data-pinme-menu-open]');
    const closeButton = drawer?.querySelector('.pinme-drawer__close');
    const nav = root.querySelector('.pinme-desktop-nav');
    const layer = root.querySelector('[data-pinme-desktop-submenu-layer]');
    const localizationPanels = [...root.querySelectorAll('.pinme-desktop-localization,[data-pinme-mobile-localization]')];
    if (!drawer || !panel || !openButton || !layer) return;
    const media = matchMedia('(min-width:1080px)');
    const mobileMotionMedia = matchMedia('(max-width:767px)');
    const reducedMotion = matchMedia('(prefers-reduced-motion:reduce)');
    const desktop = () => media.matches && root.dataset.pinmeDesktopFit !== 'compact';
    const desktopTransparent = root.dataset.pinmeDesktopTransparent === 'true';
    const mobileTransparent = root.dataset.pinmeMobileTransparent === 'true';
    const lightAtTop = mobileTransparent && root.dataset.pinmeTransparentInk !== 'dark';
    const homeHero = document.body.dataset.pinmePageKind === 'home' ? document.querySelector('[data-pinme-home-native="hero"]') : null;
    const triggers = [...nav.querySelectorAll('[data-pinme-desktop-nav-item]')].filter((item) => item.dataset.pinmeDesktopNavItem !== '').map((item) => item.querySelector('button,a'));
    const desktopPanels = [...layer.querySelectorAll('[data-pinme-desktop-submenu-panel]')];
    let activeTrigger = null, closeTimer = 0, desktopReleaseTimer = 0, releaseTimer = 0, headFrame = 0, fitFrame = 0, scrollState = null, previousFocus = null;
    let lastY = Math.max(0, window.scrollY), directionDistance = 0, previousDirection = 0;
    let mobileHomePastHero = false, layoutWidth = innerWidth;
    const inertState = new Map();
    const disclosureAnimations = new Map();
    // Keep the artwork at its maximum size, but slide it to the current panel's
    // bottom edge. The menu uses only the space its visible links need.
    const desktopPanelHeight = (expanded = false) => {
      if (!expanded) return layer.offsetHeight;
      const measure = layer.cloneNode(true);
      measure.classList.add('pinme-desktop-submenu-layer--measure');
      measure.removeAttribute('data-pinme-desktop-submenu-layer');
      measure.setAttribute('aria-hidden', 'true'); measure.inert = true;
      measure.querySelectorAll('[id]').forEach((entry) => entry.removeAttribute('id'));
      measure.querySelectorAll('.pinme-native-third-level').forEach((entry) => {
        if (expanded) entry.hidden = false;
        else if (entry.hasAttribute('aria-hidden')) entry.hidden = entry.getAttribute('aria-hidden') === 'true';
        ['height','overflow','opacity','transform','marginTop'].forEach((property) => { entry.style[property] = ''; });
      });
      if (expanded) measure.querySelectorAll('[data-pinme-desktop-branch]').forEach((entry) => entry.classList.add('is-open'));
      const panels = [...measure.querySelectorAll('[data-pinme-desktop-submenu-panel]')];
      if (expanded) panels.forEach((entry) => { entry.hidden = true; });
      desktopHead.append(measure);
      let height = expanded ? 0 : measure.offsetHeight;
      if (expanded) panels.forEach((entry) => { entry.hidden = false; height = Math.max(height, measure.offsetHeight); entry.hidden = true; });
      measure.remove();
      return height;
    };
    const syncDesktopSurface = () => {
      if (!desktop() || !desktopTransparent) return;
      const maximum = parseFloat(root.style.getPropertyValue('--nav-desktop-surface-height')) || desktopHead.offsetHeight;
      const current = activeTrigger ? Math.max(desktopHead.offsetHeight, layer.offsetTop + desktopPanelHeight()) : desktopHead.offsetHeight;
      const shift = Math.min(0, current - maximum);
      root.style.setProperty('--nav-desktop-surface-shift', `${shift}px`);
      root.style.setProperty('--nav-desktop-surface-clip', `${-shift}px`);
    };
    const measureDesktopMenu = () => {
      if (!desktop() || !desktopTransparent) return;
      const height = desktopPanelHeight(true);
      root.style.setProperty('--nav-desktop-panel-height', `${height}px`);
      root.style.setProperty('--nav-desktop-surface-height', `${Math.max(desktopHead.offsetHeight, layer.offsetTop + height)}px`);
      syncDesktopSurface();
    };
    const finishDisclosure = (content, open) => {
      content.hidden = !open;
      content.inert = !open;
      ['height','overflow','opacity','transform','marginTop'].forEach((property) => { content.style[property] = ''; });
      if (layer.contains(content)) syncDesktopSurface();
    };
    const settleDisclosures = () => {
      disclosureAnimations.forEach((animation) => animation.cancel());
      disclosureAnimations.clear();
    };
    const disclosureFollowers = (content) => {
      const nodes = new Set(disclosureAnimations.keys());
      if (drawer.contains(content)) {
        for (let entry = content; entry && entry !== flow; entry = entry.parentElement) {
          for (let sibling = entry.nextElementSibling; sibling; sibling = sibling.nextElementSibling) nodes.add(sibling);
        }
      }
      return [...nodes].filter((node) => node !== content && visible(node));
    };
    const animateDisclosure = (node, keyframes) => {
      const animation = node.animate(keyframes,{duration:220,easing:'cubic-bezier(.2,.7,.2,1)',fill:'both'});
      disclosureAnimations.set(node,animation);
      animation.onfinish = () => {
        if (disclosureAnimations.get(node) !== animation) return;
        disclosureAnimations.delete(node); animation.cancel();
      };
    };
    const setDisclosure = (trigger, open) => {
      if (!trigger) return;
      const content = root.querySelector(`#${CSS.escape(trigger.getAttribute('aria-controls'))}`);
      if (!content) return;
      const wasOpen = trigger.getAttribute('aria-expanded') === 'true';
      if (wasOpen === open) return;
      const desktopBranch = layer.contains(trigger) && desktop();
      const animate = (drawer.contains(trigger) && !desktop() || desktopBranch && activeTrigger) && !reducedMotion.matches && typeof content.animate === 'function';
      const followers = animate ? disclosureFollowers(content).map((node) => ({node,before:node.getBoundingClientRect(),opacity:Number(getComputedStyle(node).opacity)})) : [];
      settleDisclosures();
      trigger.setAttribute('aria-expanded', String(open));
      content.setAttribute('aria-hidden', String(!open));
      trigger.closest('.pinme-drawer-group,.pinme-native-mobile-branch,.pinme-native-desktop-group')?.classList.toggle('is-open', open);
      finishDisclosure(content,open);
      if (!animate) return;
      const moves = followers.filter(({node}) => visible(node)).map((entry) => {
        const after = entry.node.getBoundingClientRect();
        return {...entry,x:entry.before.left-after.left,y:entry.before.top-after.top};
      });
      moves.forEach((entry) => {
        const ancestor = moves.filter((other) => other !== entry && other.node.contains(entry.node)).sort((a,b) => a.node.contains(b.node) ? 1 : -1)[0];
        const x = entry.x-(ancestor?.x || 0), y = entry.y-(ancestor?.y || 0);
        if (Math.abs(x)<.5 && Math.abs(y)<.5 && entry.opacity>=.99) return;
        animateDisclosure(entry.node,[{transform:`translate3d(${x}px,${y}px,0)`,opacity:entry.opacity},{transform:'translate3d(0,0,0)',opacity:1}]);
      });
      if (open) animateDisclosure(content,[{opacity:0,transform:'translate3d(0,-4px,0)'},{opacity:1,transform:'translate3d(0,0,0)'}]);
    };
    const finishDesktopClose = () => {
      desktopReleaseTimer = 0;
      desktopPanels.forEach((entry) => { entry.hidden = true; entry.inert = true; });
      layer.querySelectorAll('[data-pinme-desktop-branch-trigger]').forEach((trigger) => setDisclosure(trigger, false));
      root.classList.remove('has-desktop-submenu-surface');
    };
    const closeDesktop = ({ immediate = false } = {}) => {
      clearTimeout(closeTimer);
      if (!activeTrigger && desktopReleaseTimer && !immediate && !reducedMotion.matches) return;
      clearTimeout(desktopReleaseTimer);
      const wasVisible = root.classList.contains('has-desktop-submenu-surface');
      activeTrigger = null;
      root.classList.remove('has-desktop-submenu-open');
      syncDesktopSurface();
      layer.classList.remove('is-open');
      layer.inert = true;
      triggers.forEach((trigger) => { trigger.setAttribute('aria-expanded', 'false'); trigger.parentElement.classList.remove('is-open'); });
      desktopPanels.forEach((entry) => { entry.inert = true; });
      // Disable interaction now; retain the row's dimensions until it fades out.
      if (immediate || reducedMotion.matches || !wasVisible) finishDesktopClose();
      else desktopReleaseTimer = setTimeout(finishDesktopClose, 280);
    };
    const openDesktop = (trigger) => {
      if (!desktop()) return;
      if (desktopTransparent && !root.style.getPropertyValue('--nav-desktop-panel-height')) measureDesktopMenu();
      clearTimeout(closeTimer);
      clearTimeout(desktopReleaseTimer); desktopReleaseTimer = 0;
      activeTrigger = trigger;
      const key = trigger.parentElement.dataset.pinmeDesktopNavItem;
      root.classList.add('has-desktop-submenu-surface');
      layer.classList.add('is-open');
      layer.inert = false;
      triggers.forEach((entry) => { const selected = entry === trigger; entry.setAttribute('aria-expanded', String(selected)); entry.parentElement.classList.toggle('is-open', selected); });
      desktopPanels.forEach((entry) => { const selected = entry.dataset.pinmeDesktopSubmenuPanel === key; entry.hidden = !selected; entry.inert = !selected; });
      syncDesktopSurface();
      root.classList.add('has-desktop-submenu-open');
    };
    const scheduleDesktopClose = (fromFocus = false) => {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        const focused = nav.contains(document.activeElement) || layer.contains(document.activeElement);
        if (!focused && (fromFocus || !nav.matches(':hover') && !layer.matches(':hover'))) closeDesktop();
      }, 160);
    };
    triggers.forEach((trigger) => {
      listen(trigger.parentElement, 'pointerenter', (event) => { if (event.pointerType !== 'touch') openDesktop(trigger); });
      listen(trigger, 'focus', () => openDesktop(trigger));
      listen(trigger, 'click', () => openDesktop(trigger));
      listen(trigger, 'keydown', (event) => {
        if (!['ArrowDown','ArrowUp'].includes(event.key)) return;
        event.preventDefault(); openDesktop(trigger);
        const entries = focusable(layer);
        (event.key === 'ArrowUp' ? entries.at(-1) : entries[0])?.focus();
      });
    });
    nav.querySelectorAll('[data-pinme-desktop-nav-item=""]').forEach((item) => {
      listen(item, 'pointerenter', closeDesktop); listen(item, 'focusin', closeDesktop);
    });
    [nav,layer].forEach((entry) => { listen(entry, 'pointerenter', () => clearTimeout(closeTimer)); listen(entry, 'pointerleave', () => scheduleDesktopClose()); });
    listen(root, 'focusout', (event) => { if (!nav.contains(event.relatedTarget) && !layer.contains(event.relatedTarget)) scheduleDesktopClose(true); });
    layer.querySelectorAll('[data-pinme-desktop-branch]').forEach((branch) => {
      const trigger = branch.querySelector('[data-pinme-desktop-branch-trigger]');
      listen(branch, 'pointerenter', (event) => { if (event.pointerType !== 'touch') setDisclosure(trigger, true); });
      listen(branch, 'pointerleave', (event) => { if (event.pointerType !== 'touch' && layer.contains(event.relatedTarget) && !branch.contains(document.activeElement)) setDisclosure(trigger, false); });
      listen(branch, 'focusin', (event) => { if (event.target !== trigger) setDisclosure(trigger, true); });
      listen(branch, 'focusout', (event) => { if (layer.contains(event.relatedTarget) && !branch.contains(event.relatedTarget) && !branch.matches(':hover')) setDisclosure(trigger, false); });
      listen(trigger, 'click', () => setDisclosure(trigger, trigger.getAttribute('aria-expanded') !== 'true'));
    });
    root.querySelectorAll('[data-pinme-submenu-trigger]').forEach((trigger) => {
      listen(trigger, 'click', () => {
        const open = trigger.getAttribute('aria-expanded') !== 'true';
        root.querySelectorAll('[data-pinme-submenu-trigger]').forEach((entry) => setDisclosure(entry, entry === trigger && open));
      });
    });
    root.querySelectorAll('[data-pinme-nested-trigger]').forEach((trigger) => listen(trigger, 'click', () => setDisclosure(trigger, trigger.getAttribute('aria-expanded') !== 'true')));
    const setBackgroundInert = (locked) => {
      if (locked) {
        [document.querySelector('#MainContent'),document.querySelector('main'),...document.querySelectorAll('footer,#footer-group,.shopify-section-group-footer-group,.pinme-custom-footer')].filter(Boolean).forEach((node) => {
          if (node.contains(root) || inertState.has(node)) return;
          inertState.set(node,node.inert); node.inert = true;
        });
        mobileHead.inert = true; desktopHead.inert = true;
      } else {
        inertState.forEach((value,node) => { if (node.isConnected) node.inert = value; }); inertState.clear();
        mobileHead.inert = false; desktopHead.inert = false;
      }
    };
    const lock = () => {
      if (scrollState) return;
      const body = document.body, html = document.documentElement;
      if (window.PinmeUI?.lockScroll) {
        const y = body.style.position === 'fixed' ? Math.max(0,-parseFloat(body.style.top)||0) : scrollY;
        scrollState = { y,sharedRelease:window.PinmeUI.lockScroll(`pinme-navigation-${root.id || root.className}`) };
        body.classList.add('pinme-drawer-lock'); html.classList.add('pinme-drawer-lock'); setBackgroundInert(true); return;
      }
      const names = ['position','top','left','right','width','overflow','paddingRight','overscrollBehavior'];
      const bodyStyle = Object.fromEntries(names.map((key) => [key,body.style[key]]));
      scrollState = { x:scrollX,y:scrollY,bodyStyle,htmlOverflow:html.style.overflow,htmlScroll:html.style.scrollBehavior };
      const compensation = Math.max(0,innerWidth-html.clientWidth);
      html.style.overflow = 'hidden';
      Object.assign(body.style,{position:'fixed',top:`-${scrollState.y}px`,left:`-${scrollState.x}px`,right:'0',width:'100%',overflow:'hidden',overscrollBehavior:'none'});
      if (compensation) body.style.paddingRight = `${(parseFloat(getComputedStyle(body).paddingRight)||0)+compensation}px`;
      body.classList.add('pinme-drawer-lock'); html.classList.add('pinme-drawer-lock');
      setBackgroundInert(true);
    };
    const unlock = ({ handoff = false } = {}) => {
      if (!scrollState) return;
      const state = scrollState; scrollState = null;
      if (state.sharedRelease) {
        document.body.classList.remove('pinme-drawer-lock'); document.documentElement.classList.remove('pinme-drawer-lock');
        setBackgroundInert(false); if (handoff) queueMicrotask(state.sharedRelease); else state.sharedRelease(); lastY = state.y; return;
      }
      Object.assign(document.body.style,state.bodyStyle);
      document.documentElement.style.overflow = state.htmlOverflow;
      document.documentElement.style.scrollBehavior = 'auto';
      document.body.classList.remove('pinme-drawer-lock'); document.documentElement.classList.remove('pinme-drawer-lock');
      setBackgroundInert(false); window.scrollTo(state.x,state.y); lastY = state.y;
      requestAnimationFrame(() => { document.documentElement.style.scrollBehavior = state.htmlScroll; });
    };
    const setHeadState = (state) => {
      if (root.dataset.pinmeMobileState !== state) root.dataset.pinmeMobileState = state;
      const ink = lightAtTop && !mobileHomePastHero && (state === 'top' || mobileMotionMedia.matches) ? 'light' : 'dark';
      if (root.dataset.pinmeMobileInk !== ink) root.dataset.pinmeMobileInk = ink;
    };
    const updateMobileLogo = (y) => {
      const wasPastHero = mobileHomePastHero;
      // Once the phone header reaches the light home sections, preserve the
      // existing solid reveal. The boundary follows the actual hero height.
      mobileHomePastHero = mobileMotionMedia.matches && !!homeHero && homeHero.getBoundingClientRect().bottom <= mobileHead.offsetHeight + (parseFloat(getComputedStyle(mobileHead).top) || 0);
      if (mobileMotionMedia.matches) {
        if (homeHero) root.dataset.pinmeMobileHomePastHero = String(mobileHomePastHero);
        // The home logo shares the white icons over the video from the first
        // frame. Its colour changes at the hero boundary, not after scrolling.
        root.style.setProperty('--nav-mobile-logo-white',String(lightAtTop && !mobileHomePastHero ? 1 : 0));
      } else {
        delete root.dataset.pinmeMobileHomePastHero;
        root.style.removeProperty('--nav-mobile-logo-white');
      }
      if (wasPastHero !== mobileHomePastHero) setHeadState(root.dataset.pinmeMobileState || (y<=4?'top':'revealed'));
    };
    const closeMenu = ({ immediate = false, restore = true, handoff = false } = {}) => {
      clearTimeout(releaseTimer);
      const wasOpen = drawer.classList.contains('is-open');
      drawer.classList.remove('is-open'); drawer.setAttribute('aria-hidden','true'); drawer.inert = true; openButton.setAttribute('aria-expanded','false');
      root.querySelector('[data-pinme-mobile-localization]')?.removeAttribute('open');
      const finish = () => { unlock({handoff}); if (restore && wasOpen && visible(previousFocus)) previousFocus.focus({preventScroll:true}); };
      if (immediate || matchMedia('(prefers-reduced-motion:reduce)').matches) finish();
      else releaseTimer = setTimeout(finish, Number(root.dataset.pinmeDrawerDuration)||350);
    };
    const openMenu = () => {
      if (desktop() || drawer.classList.contains('is-open')) return;
      clearTimeout(releaseTimer); closeDesktop({immediate:true});
      document.dispatchEvent(new CustomEvent('pinme:menu-opening'));
      previousFocus = document.activeElement; lock();
      drawer.inert = false; drawer.classList.add('is-open'); drawer.setAttribute('aria-hidden','false'); openButton.setAttribute('aria-expanded','true');
      closeButton.focus({preventScroll:true});
    };
    listen(openButton,'click',openMenu);
    drawer.querySelectorAll('[data-pinme-menu-close]').forEach((entry) => listen(entry,'click',() => closeMenu()));
    listen(drawer,'click',(event) => { if (event.target.closest('a[href]')) closeMenu({restore:false}); });
    listen(document,'pinme:cart-opening',() => { closeDesktop({immediate:true}); closeMenu({immediate:true,restore:false,handoff:true}); });
    listen(document,'pointerdown',(event) => {
      if (!nav.contains(event.target) && !layer.contains(event.target)) closeDesktop();
      localizationPanels.forEach((entry) => { if (entry.open && !entry.contains(event.target)) entry.open = false; });
    },{passive:true});
    listen(document,'keydown',(event) => {
      if (event.key === 'Escape') {
        const localization = localizationPanels.find(entry => entry.open);
        if (localization) { event.preventDefault(); localization.open = false; localization.querySelector('summary')?.focus({preventScroll:true}); return; }
        if (drawer.classList.contains('is-open')) { event.preventDefault(); closeMenu(); }
        else if (activeTrigger) { event.preventDefault(); const trigger = activeTrigger; closeDesktop(); trigger.focus({preventScroll:true}); closeDesktop(); }
      }
      if (event.key !== 'Tab' || !drawer.classList.contains('is-open')) return;
      const entries = focusable(panel), first = entries[0], last = entries.at(-1), active = document.activeElement;
      if (!first) { event.preventDefault(); panel.focus(); return; }
      if (event.shiftKey && (active === first || !panel.contains(active))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (active === last || !panel.contains(active))) { event.preventDefault(); first.focus(); }
    });
    listen(root,'change',(event) => { if (event.target.matches('[data-pinme-country-select]')) event.target.form?.requestSubmit(); });
    const positionMobileCountry = (entry) => {
      const summary = entry.querySelector('summary'), countryPanel = entry.querySelector('.pinme-drawer__country-panel');
      if (!summary || !countryPanel) return;
      if (!drawer.classList.contains('is-open') || desktop()) { ['--nav-mobile-country-width','--nav-mobile-country-height','--nav-mobile-country-bottom'].forEach(property => countryPanel.style.removeProperty(property)); return; }
      const anchor = summary.getBoundingClientRect(), bounds = panel.getBoundingClientRect();
      const bottom = Math.min(anchor.bottom, bounds.bottom - 16);
      countryPanel.style.setProperty('--nav-mobile-country-width',`${Math.max(72,Math.min(120,bounds.right-anchor.right-20))}px`);
      countryPanel.style.setProperty('--nav-mobile-country-bottom',`${Math.max(0,anchor.bottom-bottom)}px`);
      countryPanel.style.setProperty('--nav-mobile-country-height',`${Math.max(0,bottom-bounds.top-16)}px`);
    };
    localizationPanels.forEach(entry => listen(entry,'toggle',() => {
      if (!entry.open) return;
      localizationPanels.forEach(other => { if (other !== entry) other.open = false; });
      if (entry.matches('[data-pinme-mobile-localization]')) {
        positionMobileCountry(entry);
      }
    }));
    listen(root,'error',(event) => { const image = event.target; if (image.matches?.('img[data-pinme-image-fallback]') && !image.dataset.pinmeFallbackApplied) { image.dataset.pinmeFallbackApplied = 'true'; image.src = image.dataset.pinmeImageFallback; } },{capture:true});
    root.querySelectorAll('img[data-pinme-image-fallback]').forEach((image) => { if (image.complete && !image.naturalWidth) { image.dataset.pinmeFallbackApplied = 'true'; image.src = image.dataset.pinmeImageFallback; } });
    const updateCounts = (event) => {
      const cart = event.detail?.resource || event.detail?.cart;
      const count = cart?.item_count ?? event.detail?.data?.itemCount;
      if (!Number.isInteger(count) || count < 0) return;
      root.querySelectorAll('.cart-bubble').forEach((bubble) => { bubble.classList.toggle('visually-hidden',count===0); bubble.setAttribute('aria-hidden',String(count===0)); const text = bubble.querySelector('.cart-bubble__text-count'); if (text) text.textContent = count > 99 ? '99+' : String(count); });
    };
    ['cart:update','pinme:cart-synced'].forEach((event) => listen(document,event,updateCounts));
    const updateHead = () => {
      headFrame = 0;
      if (scrollState || document.body.classList.contains('pinme-cart-lock') || document.body.classList.contains('pinme-ui-scroll-locked')) return;
      const y = Math.max(0,scrollY), delta = y-lastY; lastY = y;
      root.classList.toggle('is-scrolled',desktopTransparent && y>30);
      if (desktop()) return;
      updateMobileLogo(y);
      if (y<=4) { directionDistance=0; previousDirection=0; setHeadState('top'); return; }
      const direction = Math.sign(delta);
      if (direction && direction !== previousDirection) directionDistance=0;
      if (direction) previousDirection=direction;
      directionDistance += delta;
      if (directionDistance>6) { setHeadState('hidden'); directionDistance=0; }
      else if (directionDistance<-6) { setHeadState('revealed'); directionDistance=0; }
      else if (root.dataset.pinmeMobileState==='top') setHeadState('revealed');
    };
    const fitNav = () => {
      fitFrame=0;
      root.dataset.pinmeDesktopFit = media.matches && root.dataset.pinmeDesktopFit === 'compact' ? 'measuring' : 'ready';
      root.style.setProperty('--nav-fit-gap',getComputedStyle(root).getPropertyValue('--nav-gap').trim()||'30px');
      if (!media.matches) { root.dataset.pinmeDesktopFit='ready'; closeDesktop({immediate:true}); return; }
      const inner = nav.querySelector('.pinme-desktop-nav__inner'), logo = root.querySelector('.pinme-desktop-logo');
      const count = Math.max(1,inner.children.length-1), configuredGap = parseFloat(getComputedStyle(root).getPropertyValue('--nav-gap'))||30;
      const limit = logo.getBoundingClientRect().left-24;
      const edge = () => Math.max(...[...inner.children].map((item) => item.getBoundingClientRect().right));
      const overflow = edge()-limit;
      if (overflow>0) root.style.setProperty('--nav-fit-gap',`${Math.max(8,configuredGap-overflow/count)}px`);
      if (edge()>limit+1) { root.dataset.pinmeDesktopFit='compact'; closeDesktop({immediate:true}); }
      else {
        root.dataset.pinmeDesktopFit='ready';
        if (drawer.classList.contains('is-open')) closeMenu({immediate:true,restore:false});
      }
      measureDesktopMenu();
    };
    const scheduleFit = () => { if (!fitFrame) fitFrame=requestAnimationFrame(fitNav); };
    const viewport = () => {
      const height = `${window.visualViewport?.height||innerHeight}px`;
      if (root.style.getPropertyValue('--nav-viewport-height') !== height) root.style.setProperty('--nav-viewport-height',height);
      localizationPanels.filter(entry => entry.open && entry.matches('[data-pinme-mobile-localization]')).forEach(entry => {
        if (drawer.classList.contains('is-open') && !desktop()) {
          const anchor = entry.querySelector('summary').getBoundingClientRect(), bounds = flow.getBoundingClientRect();
          if (anchor.bottom > bounds.bottom - 16) flow.scrollTop += anchor.bottom - (bounds.bottom - 16);
          else if (anchor.top < bounds.top + 16) flow.scrollTop -= bounds.top + 16 - anchor.top;
        }
        positionMobileCountry(entry);
      });
    };
    listen(flow,'scroll',() => localizationPanels.filter(entry => entry.open && entry.matches('[data-pinme-mobile-localization]')).forEach(positionMobileCountry),{passive:true});
    listen(window,'scroll',() => { if (!headFrame) headFrame=requestAnimationFrame(updateHead); },{passive:true});
    listen(window,'resize',() => {
      viewport();
      if (innerWidth === layoutWidth) return;
      layoutWidth = innerWidth; scheduleFit();
      lastY=scrollY; setHeadState(scrollY<=4?'top':'revealed'); updateMobileLogo(scrollY);
      settleDisclosures();
    },{passive:true});
    listen(window.visualViewport,'resize',viewport,{passive:true});
    listen(reducedMotion,'change',() => { if (reducedMotion.matches) { settleDisclosures(); if (desktopReleaseTimer) closeDesktop({immediate:true}); } });
    listen(document.fonts,'loadingdone',scheduleFit);
    document.fonts?.ready.then(() => { if (!controller.signal.aborted) scheduleFit(); });
    const observer = typeof ResizeObserver==='function' ? new ResizeObserver(scheduleFit) : null;
    observer?.observe(root.querySelector('.pinme-desktop-nav__inner'));
    listen(layer,'load',scheduleFit,{capture:true});
    setHeadState(lastY<=4?'top':'revealed'); updateMobileLogo(lastY); viewport(); updateHead(); scheduleFit();
    instances.set(root,() => { closeMenu({immediate:true,restore:false}); closeDesktop({immediate:true}); controller.abort(); observer?.disconnect(); disclosureAnimations.forEach(animation => animation.cancel()); disclosureAnimations.clear(); clearTimeout(closeTimer); clearTimeout(releaseTimer); cancelAnimationFrame(headFrame); cancelAnimationFrame(fitFrame); ['--nav-desktop-panel-height','--nav-desktop-surface-height','--nav-desktop-surface-shift','--nav-desktop-surface-clip'].forEach((property) => root.style.removeProperty(property)); instances.delete(root); });
  };
  const init = (scope) => { if (scope.matches?.('.pinme-header-v4')) initHeader(scope); scope.querySelectorAll?.('.pinme-header-v4').forEach(initHeader); };
  window.PinmeNavigationV4 = { init };
  document.addEventListener('shopify:section:load',(event) => init(event.target));
  document.addEventListener('shopify:section:unload',(event) => { instances.forEach((destroy,root) => { if (event.target.contains(root)) destroy(); }); });
  document.addEventListener('shopify:block:select',(event) => {
    const root = event.target.closest('.pinme-header-v4'), group = event.target.closest('.pinme-drawer-group');
    if (!root || !group) return;
    if (matchMedia('(min-width:1080px)').matches && root.dataset.pinmeDesktopFit !== 'compact') {
      const index = group.style.getPropertyValue('--group-index').trim();
      root.querySelector(`[data-pinme-desktop-nav-item="${CSS.escape(index)}"] .pinme-desktop-nav__link`)?.focus();
      return;
    }
    if (root.querySelector('[data-pinme-drawer]').getAttribute('aria-hidden') !== 'false') root.querySelector('[data-pinme-menu-open]').click();
    const trigger = group.querySelector('[data-pinme-submenu-trigger]');
    if (trigger && trigger.getAttribute('aria-expanded')!=='true') trigger.click();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',() => init(document),{once:true}); else init(document);
})();

